import { useEffect, useState } from "react";
import type { Coordinates } from "../shared/types";

export function useLocationOverride(deviceId: string, position: Coordinates | null) {
	const [error, setError] = useState<string | null>(null);
	const [syncing, setSyncing] = useState(false);
	const lat = position?.lat ?? null;
	const lon = position?.lon ?? null;
	useEffect(() => {
		let disposed = false;
		let busy = false;
		let done = false;
		const key = `gotime.locationOverride.${deviceId}`;
		const signature = JSON.stringify({ lat, lon });
		let previous: { signature?: string; revisionMs?: number } | null = null;
		try { previous = JSON.parse(localStorage.getItem(key) ?? "null"); } catch {}
		const revisionMs = previous?.signature === signature && typeof previous.revisionMs === "number" ? previous.revisionMs : Math.max(Date.now(), (previous?.revisionMs ?? 0) + 1);
		try { localStorage.setItem(key, JSON.stringify({ signature, revisionMs })); } catch {}
		setSyncing(true);
		setError(null);
		async function sync() {
			if (busy || done || disposed) return;
			busy = true;
			try {
				const response = await fetch("/api/location-override", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ deviceId, position: lat === null || lon === null ? null : { lat, lon }, revisionMs }) });
				if (response.status === 403 && lat === null) { done = true; return; }
				if (!response.ok) throw new Error(response.status === 409 ? "Location override changed elsewhere. Select your location again." : "Location override not synced. Keep the app open to retry.");
				done = true;
				if (!disposed) setError(null);
			} catch (cause) {
				if (!disposed) setError(cause instanceof Error ? cause.message : "Location override not synced.");
			} finally {
				busy = false;
				if (!disposed) setSyncing(false);
			}
		}
		void sync();
		const retry = () => { if (document.visibilityState === "visible") void sync(); };
		const timer = window.setInterval(retry, 15_000);
		window.addEventListener("online", retry);
		document.addEventListener("visibilitychange", retry);
		return () => { disposed = true; window.clearInterval(timer); window.removeEventListener("online", retry); document.removeEventListener("visibilitychange", retry); };
	}, [deviceId, lat, lon]);
	return { error, syncing };
}